import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getActiveStep } from '../../../redux/toolsSlice';
import { ELEMENTS } from './components/authoring/content/Consts';
import { LargeTextContentPreview } from './components/authoring/content/fields/LargeTextContent';
import { BodyTextContentPreview } from './components/authoring/content/fields/BodyTextContent';
import { VideoContentPreview } from './components/authoring/content/fields/VideoContent';
import { ButtonContentPreview } from './components/authoring/content/fields/ButtonContent';
import EditorPreviewEmbed from '../walkthroughs/EditorPreviewEmbed';
import { ImageContentPreview } from './components/authoring/content/fields/ImageContent';
import { EmbedCodeContentPreview } from './components/authoring/content/fields/EmbedCodeContent';
import { AttachmentContentPreview } from './components/authoring/content/fields/AttachmentContent';
import { ShortAnswerContentPreview } from './components/authoring/content/fields/ShortAnswerContent';
import { LongAnswerContentPreview } from './components/authoring/content/fields/LongAnswerContent';
import { CheckboxContentPreview } from './components/authoring/content/fields/CheckboxContent';
import { MultiSelectContentPreview } from './components/authoring/content/fields/MultiSelectContent';
import { FileUploadContentPreview } from './components/authoring/content/fields/FileUploadContent';
import { DateIcon } from '../../assets/Icons';
import ButtonGroup from './components/authoring/content/fields/ButtonGroup';
import moment from 'moment';
import { MultipleChoiceContentView } from './components/authoring/content/fields/MultipleChoiceContent';
import { getResponseData } from '../../../redux/hubSlice';

const DEFAULT_STEP_DESCRIPTION = 'Add a description to this step';

// Form elements that count toward "all elements" completion gating.
const FORM_TYPES = [ELEMENTS.SHORT_ANSWER, ELEMENTS.LONG_ANSWER, ELEMENTS.MULTIPLE_CHOICE, ELEMENTS.CHECKBOX, ELEMENTS.MULTI_SELECT];

// Learner walkthrough render honoring the author's launch style: inline (default),
// a "Start" button that reveals the embed, or a modal opened from that button.
const WalkthroughEmbed = ({ layer }) => {
  const [open, setOpen] = useState(false);
  const style = layer?.launchStyle || 'inline';
  const label = layer?.buttonLabel || 'Start the walkthrough';
  const embed = <EditorPreviewEmbed id={layer?.source} layer={layer} />;

  if (!layer?.source || style === 'inline') return embed;

  if (style === 'button') {
    return open ? embed : (
      <div style={{ textAlign: 'center' }}>
        <button type="button" className="un-pe-btnel un-pe-btnel--primary" onClick={() => setOpen(true)}>{label}</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <button type="button" className="un-pe-btnel un-pe-btnel--primary" onClick={() => setOpen(true)}>{label}</button>
      </div>
      {open && (
        <Modal show onHide={() => setOpen(false)} dialogClassName="un-wt-modal">
          <Modal.Header closeButton><Modal.Title>Walkthrough</Modal.Title></Modal.Header>
          <Modal.Body>{embed}</Modal.Body>
        </Modal>
      )}
    </>
  );
};

const StepPreviewContent = ({ moveToNextStep, isComplete, stepIndex = 0, totalSteps = 0, goToStep }) => {
  const activeStep = useSelector((state) => getActiveStep(state));
  const { response } = useSelector((state) => getResponseData(state));
  const [launchWalkthrough, setLaunchWalkthrough] = useState(null);

  const activeStepResponses = response?.filter((entry) => entry?.id === activeStep.id)?.[0];
  const attempts = Array.isArray(activeStepResponses?.attempt) ? activeStepResponses.attempt : [];

  const isAnswered = (layer) => {
    const a = attempts.find((entry) => entry?.id === layer.id);
    return Boolean(a && String(a.answer ?? '').trim().length > 0);
  };

  // Button actions on the learner side: open a link, mark the step complete,
  // jump to another step, or launch a walkthrough in a modal.
  const handleButtonAction = (layer) => {
    const action = layer?.action || 'link';
    if (action === 'complete') moveToNextStep?.();
    else if (action === 'link' && layer.destination) window.open(layer.destination, '_blank', 'noopener');
    else if (action === 'step' && goToStep && layer.destination) goToStep(layer.destination);
    else if (action === 'walkthrough' && layer.destination) setLaunchWalkthrough(layer.destination);
  };

  const renderElement = (layer) => {
    const layerResponse = attempts?.filter((entry) => entry?.id === layer.id);

    switch (layer.type) {
      case ELEMENTS.LARGE_TEXT:
        return <LargeTextContentPreview layer={layer} />;
      case ELEMENTS.BODY:
        return <BodyTextContentPreview layer={layer} />;
      case ELEMENTS.IMAGE:
        return <ImageContentPreview layer={layer} />;
      case ELEMENTS.VIDEO:
        return <VideoContentPreview layer={layer} />;
      case ELEMENTS.BUTTON:
        return <ButtonContentPreview layer={layer} onClick={() => handleButtonAction(layer)} />;
      case ELEMENTS.WALKTHROUGH:
        return <WalkthroughEmbed layer={layer} />;
      case ELEMENTS.EMBEDDED_CODE:
        return <EmbedCodeContentPreview layer={layer} />;
      case ELEMENTS.ATTACHMENT:
      case ELEMENTS.ATTACHMENT_LEGACY:
        return <AttachmentContentPreview layer={layer} />;
      case ELEMENTS.SHORT_ANSWER:
        return <ShortAnswerContentPreview layer={layer} />;
      case ELEMENTS.LONG_ANSWER:
        return <LongAnswerContentPreview layer={layer} />;
      case ELEMENTS.MULTI_SELECT:
        return <MultiSelectContentPreview layer={layer} />;
      case ELEMENTS.CHECKBOX:
        return <CheckboxContentPreview layer={layer} />;
      case ELEMENTS.FILE_UPLOAD:
        return <FileUploadContentPreview layer={layer} />;
      case ELEMENTS.MULTIPLE_CHOICE:
        return <MultipleChoiceContentView layer={layer} response={layerResponse} />;
      default:
        return null;
    }
  };

  const layers = Array.isArray(activeStep?.layers) ? activeStep.layers : [];
  const primaryLayer = layers.find((layer) => layer.id === 'main') || layers.find((layer) => layer.type === ELEMENTS.LARGE_TEXT);
  const contentLayers = layers.filter((layer) => layer.id !== primaryLayer?.id);

  // Hold the Complete button when a required field is unanswered, or (if the
  // author chose "All elements" completion) until every form element is answered.
  // Both default to off, so nothing gates unless opted in. File uploads are
  // excluded — they save out-of-band and aren't in the response attempts.
  const allElementsMode = activeStep?.completion_mode === 'all_elements';
  const requiredUnanswered = contentLayers.some((layer) => layer.required && FORM_TYPES.includes(layer.type) && !isAnswered(layer));
  const allElementsUnanswered = allElementsMode && contentLayers.some((layer) => FORM_TYPES.includes(layer.type) && !isAnswered(layer));
  const completeBlocked = requiredUnanswered || allElementsUnanswered;
  const completeHint = requiredUnanswered
    ? 'Answer the required questions to continue.'
    : 'Answer all questions on this step to continue.';
  const stepDescription = activeStep?.description?.trim();
  const shouldShowDescription = Boolean(stepDescription) && stepDescription !== DEFAULT_STEP_DESCRIPTION;

  let dueSummary = '';
  if (activeStep?.dueDate) {
    const today = new Date();
    const dueDateObj = new Date(activeStep.dueDate);
    const timeDiff = dueDateObj.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDifference === 0) {
      dueSummary = 'Due today';
    } else if (daysDifference === 1) {
      dueSummary = 'Due tomorrow';
    } else if (daysDifference > 1) {
      dueSummary = `Due in ${daysDifference} days`;
    } else {
      dueSummary = 'Past due';
    }
  }

  return (
    <div className="preview-element-list-wrapper">
      <div className="path-step-content">
        <div className="path-step-content__intro">
          <div className="path-step-content__hero">
            {primaryLayer ? (
              <div className="preview-element-wrapper preview-element-wrapper--primary">
                {renderElement(primaryLayer)}
              </div>
            ) : (
              <h2 className="path-step-content__fallback-title">{activeStep?.name || `Step ${stepIndex + 1}`}</h2>
            )}
          </div>

          {activeStep?.dueDate && (
            <div className="path-step-content__meta">
              <div className="path-step-content__meta-item">
                <DateIcon />
                <span>{dueSummary}, {moment(activeStep.dueDate).format('MMMM DD, YYYY')}</span>
              </div>
            </div>
          )}

          {shouldShowDescription && (
            <p className="path-step-content__description">{stepDescription}</p>
          )}
        </div>

        {contentLayers.length > 0 && (
          <div className="path-step-content__body">
            {contentLayers.map((layer, index) => (
              <div key={`${layer.id}_${index}`} className="preview-element-wrapper">
                {renderElement(layer)}
              </div>
            ))}
          </div>
        )}

        <div className="path-step-content__footer">
          <ButtonGroup
            moveToNextStep={moveToNextStep}
            isComplete={isComplete}
            blocked={completeBlocked}
            hint={completeBlocked ? completeHint : undefined}
          />
          {totalSteps > 1 && (
            <p className="path-step-content__footer-note">Step {stepIndex + 1} of {totalSteps}</p>
          )}
        </div>
      </div>

      {launchWalkthrough && (
        <Modal show onHide={() => setLaunchWalkthrough(null)} dialogClassName="un-wt-modal">
          <Modal.Header closeButton><Modal.Title>Walkthrough</Modal.Title></Modal.Header>
          <Modal.Body><EditorPreviewEmbed id={launchWalkthrough} /></Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default StepPreviewContent;
